var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ToastUtils from '@/utils/ToastUtils';
import useAppComponents from './useAppComponents';
/**
 * SccContentDetails 캐시의 isSaved/sccContentId만 즉시 toggle하기 위한 query key prefix.
 * 실제 cache key 는 [PREFIX, url] 형태로 호출처와 일치시킨다.
 * 서버가 URL pattern 으로 upvote target 을 추론하므로 URL 만으로 unique 하다.
 * 서버 응답 + invalidate refetch 사이의 지연을 가리기 위함.
 */
const SCC_CONTENT_DETAILS_QUERY_KEY_PREFIX = 'SccContentDetails';
export function useSaveContent(options) {
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ url, contentType, title, imageUrls, description, currentIsSaved, currentSccContentId, }) {
            if (currentIsSaved) {
                // sccContentId가 있으면 그걸 우선 사용하고, 없으면 URL로 unsave.
                // 직전 save 응답 도착 전 빠른 탭 케이스에서도 URL fallback 으로 안전하게 처리된다.
                yield api.unsaveContent(currentSccContentId ? { sccContentId: currentSccContentId } : { url });
                return { isSaved: false, sccContentId: currentSccContentId !== null && currentSccContentId !== void 0 ? currentSccContentId : undefined };
            }
            else {
                const { data } = yield api.saveContent({
                    url,
                    contentType,
                    webPageDetail: Object.assign(Object.assign({ imageUrls }, (title != null ? { title } : {})), (description != null ? { description } : {})),
                });
                return { isSaved: true, sccContentId: data.sccContentId };
            }
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ url, currentIsSaved, currentSccContentId }) {
            const cacheKey = [SCC_CONTENT_DETAILS_QUERY_KEY_PREFIX, url];
            yield queryClient.cancelQueries({ queryKey: cacheKey });
            const prev = queryClient.getQueryData(cacheKey);
            queryClient.setQueryData(cacheKey, old => {
                if (!old) {
                    return Object.assign(Object.assign({}, (currentSccContentId
                        ? { sccContentId: currentSccContentId }
                        : {})), { isSaved: !currentIsSaved });
                }
                return Object.assign(Object.assign({}, old), { isSaved: !currentIsSaved });
            });
            return { prev, cacheKey };
        }),
        onSuccess: ({ isSaved, sccContentId }, variables, context) => {
            var _a;
            const cacheKey = context.cacheKey;
            // 새로 저장된 경우 캐시에 sccContentId도 반영
            queryClient.setQueryData(cacheKey, old => {
                if (!old) {
                    return {
                        sccContentId,
                        isSaved,
                    };
                }
                return Object.assign(Object.assign({}, old), { sccContentId,
                    isSaved });
            });
            // SccContentDetails 캐시는 위에서 setQueryData 로 이미 최신 상태를 반영했으므로,
            // 즉시 refetch 를 트리거하지 않는다 (refetchType: 'none').
            // 즉시 invalidate 하면 background refetch 가 낙관적으로 채운 sccContentId 를 다시 null 로 되돌릴 수 있다.
            queryClient.invalidateQueries({
                queryKey: [SCC_CONTENT_DETAILS_QUERY_KEY_PREFIX, variables.url],
                refetchType: 'none',
            });
            // 저장 목록은 변동이 생겼으므로 refetch 가 필요.
            queryClient.invalidateQueries({ queryKey: ['SavedContents'] });
            if (isSaved) {
                ToastUtils.show('[메뉴 > 내 저장 > 저장한 컨텐츠]에 저장했습니다.');
            }
            else {
                ToastUtils.show('저장을 해제했습니다.');
            }
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, { isSaved, url: variables.url });
        },
        onError: (error, _variables, context) => {
            if ((context === null || context === void 0 ? void 0 : context.prev) !== undefined) {
                queryClient.setQueryData(context.cacheKey, context.prev);
            }
            ToastUtils.showOnApiError(error);
        },
    });
    const safeMutate = (args) => {
        if (isPending)
            return;
        mutate(args);
    };
    return safeMutate;
}
